// CallRecordSheet.tsx · v12.10.17 (#1723 T120 follow-up: namedRecord - Reports › Call record › Copy names the caller type and request, never the stored key) · 2026-10-16 · VoiceSpectrum AI
// CallRecordSheet.tsx · v12.10.17 (#1723 D12 the caller type and category are looked up by key - names on the sheet, never the stored key) · 2026-10-16 · VoiceSpectrum AI
// CallRecordSheet.tsx · v12.10.15 (#1627 practice name from the session when the settings read is late; #1629 category colour only, read from Personalization; #1636 callRecordText - one copy text for Reports and the drawer; #1666 "(from voicemail)" in Call data) · 2026-10-13 · VoiceSpectrum AI
// CallRecordSheet.tsx · v12.10.14 · 2026-10-12 · VoiceSpectrum AI
// #1620 (T88, UI-PROTO-CALL-RECORD-PRINT v1.9 LOCKED 10/12 "G locked") + #1619 (T84, drawer Print prints here - no new tab).
// ONE call record, the same everywhere: Reports › Call record (One call / Full page per call) and the message drawer's Print ▾.
//   1 the top card = the Messages list card: caller-type avatar; name · phone · language · #ref; the call line; category + labels;
//     on the right STATUS (and URGENT), the follow-up, ASSIGNED TO - small grey captions (a page has no hover). Left accent = the list
//     card's, nothing else: Read / Pending #C7D2FE · Completed #BBF7D0 · Urgent #DC2626 (wins). No Unread - a printed message was opened.
//   2 What the caller needs as rows (Caller · Wants big · Outcome · Next step highlighted · Flags); older calls - the paragraph.
//   3 Call data in two columns - a corrected value shows the new one and, struck through, what it was.
//   4 What the practice did - notes and texts sent, newest first.
//   5 Transcript from page 2 (as the drawer, T2_UI_PRINCIPLES §45): the caller in indigo + person icon at the left edge, the AI agent in
//     dark slate + AI spark, indented 24 px; the time at the right end of the speaker line.
// The header (PrintHeader) and footer (REPORT_STANDARD v1.2: "Printed … · Contains patient information · Generated by VoiceSpectrum AI" |
// Page X of Y) come from ReportFrame. CallRecordPrint (below) prints / saves one call from anywhere without leaving the screen.
import { callerTypeDisplay, categoryDisplay, requestTypeOf, requestWords, type TaxonomyItem } from '../../lib/taxonomy';   // v12.10.17 (#1723 D12)
import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { PrintFooter, PrintHeader, etTime, exportHtml, fileSafe, printReport } from '@shared-ui/ReportFrame';
import api from '../../lib/api';
import { toast } from '../../lib/toast';
import '../../styles/callRecordSheet.css';

export interface CrsNote { id: string; author: string; at: string; text: string; kind?: string | null }
export interface CrsLine { ts: string | null; speaker: string; role: 'agent' | 'caller' | 'path'; text: string; lang?: string | null }
export interface CrsField { token: string; label: string; value: string; flag?: string | null; corrected?: boolean; previous?: string | null; notCaptured?: string | null; source?: string | null }   // v12.10.15 (#1666): source
export interface CrsRows { caller: string; wants: string; outcome?: string | null; next: string; flags?: string | null }
/** The record as GET /t2/surface/messages/{id} serves it. */
export interface CrsRecord {
  id: string; referenceNo: string | null; name: string; phone: string; language: string; callerType: string; status: string; priority: string; category: string;
  labels: string[]; summary: string; aiSummary: string; startedAt: string; durationSeconds: number; reminder: string | null; reminderDue?: string | null;
  assignedTo: string | null; chartNumber?: string | null; notes: CrsNote[]; transcript: CrsLine[]; capturedFields?: CrsField[]; lineType?: string | null;
  summaryRows?: Record<string, CrsRows> | null; channel?: string;
}
/** What to show - the Reports "Include" ticks (the drawer prints everything). */
export type CrsPart = 'summary' | 'caller' | 'status' | 'labels' | 'reminder' | 'notes' | 'transcript';
export const CRS_ALL: CrsPart[] = ['summary', 'caller', 'status', 'labels', 'reminder', 'notes', 'transcript'];

const fmtPhone = (p: string) => { const d = (p || '').replace(/\D/g, ''); const l = d.length === 11 && d.startsWith('1') ? d.slice(1) : d; return l.length === 10 ? `(${l.slice(0, 3)}) ${l.slice(3, 6)}-${l.slice(6)}` : p; };
const langCode = (l: string) => { const x = (l || '').trim().toLowerCase(); const m: Record<string, string> = { english: 'en', spanish: 'es', 'español': 'es', french: 'fr', german: 'de' }; return (m[x] ?? x.slice(0, 2)).toUpperCase(); };
const callWhen = (iso: string) => new Date(iso).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
const shortWhen = (iso: string) => new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
const clock = (iso: string | null) => { if (!iso) return ''; const d = new Date(iso); return isNaN(d.getTime()) ? '' : d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', second: '2-digit' }).replace(/\s?[AP]M$/, ''); };
const dur = (s: number) => (s > 0 ? `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}` : '');
const lineWord = (t?: string | null) => { const x = (t ?? '').toLowerCase(); return x === 'mobile' ? 'Mobile phone' : x === 'landline' ? 'Landline' : x === 'voip' ? 'Internet phone' : ''; };
/** The reference as people see it - the same rule as MessagesPage.formatReference ("#36-0025"; legacy "#260902-0025"). */
export const formatRef = (referenceNo: string | null | undefined) => {
  const digits = (referenceNo ?? '').replace(/\D/g, '');
  if (digits.length !== 10) return referenceNo ? `#${referenceNo}` : '';
  return digits.startsWith('20') ? `#${digits.slice(4, 6)}-${digits.slice(6)}` : `#${digits.slice(0, 6)}-${digits.slice(6)}`;
};
const isDone = (r: CrsRecord) => r.status === 'done';
const isUrgent = (r: CrsRecord) => r.priority === 'urgent';
/** The list card's left accent (v1.9): Urgent wins, then Completed, else Read / Pending. */
export const crsAccent = (r: CrsRecord) => (isUrgent(r) && !isDone(r) ? 's-urg' : isDone(r) ? 's-done' : 's-prog');
const isPath = (l: CrsLine) => l.role === 'path' || (l.text ?? '').trimStart().startsWith('[path]');
/** Only the fields the call went through ("never reached" is not call data - #1103). */
export const crsFields = (r: CrsRecord) => (r.capturedFields ?? []).filter((f) => !(f.notCaptured ?? '').toLowerCase().startsWith('never reached'));

/** v12.10.14 (#1620, S248): Copy call data = the patient name, an underline, then one field per line; a correction keeps what it was;
 *  the fields not captured are named on the last line. Plain text - pastes cleanly into an EHR note or an email. */
export function callDataText(name: string, fields: CrsField[]): string {
  const got = fields.filter((f) => !f.notCaptured);
  const miss = fields.filter((f) => f.notCaptured);
  const head = (name || '').trim();
  const out: string[] = head ? [head, '-'.repeat(Math.max(12, head.length))] : [];
  got.forEach((f) => out.push(`${f.label}: ${f.value}${f.corrected && f.previous ? ` (corrected by the caller - was ${f.previous})` : ''}${f.source === 'voicemail' ? ' (from voicemail)' : ''}`));   // v12.10.15 (#1666)
  if (miss.length) out.push(`Not captured: ${miss.map((f) => f.label).join(', ')}`);
  return out.join('\n');
}

/** v12.10.17 (#1723 T120 follow-up): the record with its caller type and category in WORDS (key first, then name, then alias -
 *  the Messages resolution), for the plain-text copy. callRecordText prints what it is given, so every copy passes through here. */
export function namedRecord<R extends { callerType: string; category: string; capturedFields?: { token: string; value: string }[] | null }>(
  r: R, lists: { callerTypes?: readonly TaxonomyItem[]; categories?: readonly TaxonomyItem[] } | null | undefined): R {
  const cts = (lists?.callerTypes ?? []).filter((c) => c.key || c.name);
  const cats = (lists?.categories ?? []).filter((c) => c.key || c.name);
  return { ...r, callerType: callerTypeDisplay(cts, r.callerType).name,
    category: requestWords(categoryDisplay(cats, r.category), requestTypeOf(r.capturedFields)) };
}

/** v12.10.15 (#1636): ONE call record as plain text - Reports › Call record › Copy and the drawer's Print ▾ › Copy call record write the
 *  same text (moved here from CallRecordTab.recordText so both use it). The drawer also adds Call data (callData: true). */
export function callRecordText(r: CrsRecord, parts: CrsPart[], practice: string, ref: string, opts: { callData?: boolean } = {}): string {
  const has = (p: CrsPart) => parts.includes(p);
  const word = (s: string) => (s === 'done' ? 'Completed' : s === 'prog' ? 'In progress' : 'Pending');
  const lang = (l: string) => ({ en: 'English', es: 'Spanish', fr: 'French', de: 'German' } as Record<string, string>)[(l || '').slice(0, 2).toLowerCase()] ?? l;
  const when = (iso: string) => new Date(iso).toLocaleString([], { weekday: 'short', month: 'numeric', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
  const short = (iso: string) => new Date(iso).toLocaleString([], { month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  const dob = r.capturedFields?.find((f) => /(^|\.)(dob|dateOfBirth)$/i.test(f.token))?.value ?? '';
  const needs = (r.aiSummary || r.summary || '').trim();
  const out: string[] = [`${practice} · Call record · ${ref}`, r.name || fmtPhone(r.phone)];
  out.push(`Call: ${when(r.startedAt)}${dur(r.durationSeconds) ? ` · ${dur(r.durationSeconds)}` : ''} · ${lang(r.language)} · ${r.callerType} · ${r.category}`);
  if (has('caller')) {
    out.push(`Phone: ${fmtPhone(r.phone)}`);
    if (dob) out.push(`Date of birth: ${dob}`);
    if (r.chartNumber) out.push(`Chart #: ${r.chartNumber}`);
  }
  if (has('status')) out.push(`Status: ${word(r.status)}${r.assignedTo ? ` · assigned to ${r.assignedTo}` : ''}${r.priority === 'urgent' ? ' · Urgent' : ''}`);
  if (has('labels') && r.labels.length) out.push(`Labels: ${r.labels.join(', ')}`);
  if (has('reminder') && r.reminder) out.push(`Reminder: ${r.reminder}${r.reminderDue ? ` (${r.reminderDue})` : ''}`);
  if (has('summary') && needs) out.push('', 'What the caller needs:', needs);
  if (opts.callData && (r.capturedFields ?? []).length) {
    const fields = (r.capturedFields ?? []).filter((f) => !(f.notCaptured ?? '').toLowerCase().startsWith('never reached'));
    if (fields.length) out.push('', 'Call data:', callDataText(r.name || '', fields));
  }
  if (has('notes') && (r.notes ?? []).length) { out.push('', opts.callData ? 'What the practice did:' : 'Notes:'); r.notes.forEach((n) => out.push(`  ${n.at ? short(n.at) : ''} · ${n.author}: ${n.text}`)); }
  if (has('transcript') && (r.transcript ?? []).some((l) => l.role !== 'path')) {
    out.push('', 'Transcript:');
    r.transcript.filter((l) => l.role !== 'path').forEach((l) => out.push(`  ${l.role === 'caller' ? 'Caller' : 'Agent'}: ${l.text}`));
  }
  return out.join('\n');
}

function PersonIcon({ size = 14 }: { size?: number }) {
  return <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="8" r="4" /><path d="M4 21a8 8 0 0 1 16 0" /></svg>;
}
function SparkIcon() {
  return <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 3l1.8 4.7L18.5 9.5l-4.7 1.8L12 16l-1.8-4.7L5.5 9.5l4.7-1.8L12 3z" /><path d="M19 15l.8 2.2L22 18l-2.2.8L19 21l-.8-2.2L16 18l2.2-.8L19 15z" /></svg>;
}

/** The follow-up chip: "Follow-up Oct 18 (in 1 week)". */
function followUp(r: CrsRecord): string | null {
  if (!r.reminder && !r.reminderDue) return null;
  if (!r.reminderDue) return `Follow-up ${r.reminder}`;
  const d = new Date(`${r.reminderDue}T12:00:00`);
  if (isNaN(d.getTime())) return `Follow-up ${r.reminderDue}`;
  const today = new Date(); today.setHours(12, 0, 0, 0);
  const days = Math.round((d.getTime() - today.getTime()) / 86_400_000);
  const rel = days === 0 ? 'today' : days === 1 ? 'tomorrow' : days === -1 ? 'yesterday' : days > 0 ? (days % 7 === 0 ? `in ${days / 7} week${days === 7 ? '' : 's'}` : `in ${days} days`) : `${-days} days ago`;
  return `Follow-up ${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} (${rel})`;
}

export function CallRecordSheet(props: {
  r: CrsRecord; parts?: CrsPart[]; labelColors?: Record<string, string>; categoryColor?: string; categoryName?: string;
  /** the call-path lines too (the drawer, only with the Call path right and the switch on) */ withPath?: boolean;
  /** the English line under a turn that was not spoken in English (the drawer's translation, when it has one) */ english?: (string | null)[];
}) {
  const { r } = props;
  const parts = props.parts ?? CRS_ALL;
  // v12.10.15 (#1629): Reports › Call record passes no colour - read the practice's own category list (the same list the Messages
  // card uses) so the tag always has the configured colour. Colour only, no icon (Tushar S136).
  // v12.10.17 (#1723 D12): read always (the caller type's name is needed too) and resolved key first, then name, then alias.
  const cats = useQuery<{ categories?: TaxonomyItem[]; callerTypes?: TaxonomyItem[] }>({ queryKey: ['t2-personalization-cats'],
    queryFn: () => api.get('/t2/surface/personalization'), staleTime: 300_000 });
  const catD = categoryDisplay((cats.data?.categories ?? []).filter((c) => c.key || c.name), r.category);
  const catColor = props.categoryColor || catD.color || undefined;
  const catName = props.categoryName || requestWords(catD, requestTypeOf(r.capturedFields));
  const ctName = callerTypeDisplay((cats.data?.callerTypes ?? []).filter((c) => c.key || c.name), r.callerType).name;
  const has = (p: CrsPart) => parts.includes(p);
  const rows = r.summaryRows?.en ?? null;
  const paragraph = (r.aiSummary || r.summary || '').trim();
  const fields = crsFields(r);
  const missing = fields.filter((f) => f.notCaptured).length;
  const corrected = fields.filter((f) => f.corrected).length;
  const half = Math.ceil(fields.length / 2);
  const notes = [...(r.notes ?? [])].sort((a, b) => (b.at ?? '').localeCompare(a.at ?? ''));
  const lines = (r.transcript ?? []).filter((l) => props.withPath || !isPath(l));
  const spoken = lines.some((l) => !isPath(l));
  const fu = followUp(r);
  const callerFirst = (r.name || '').trim().split(/\s+/)[0]?.toUpperCase() || 'CALLER';
  const line = lineWord(r.lineType);
  return (
    <div className="crs">
      <div className={`crs-card ${crsAccent(r)}`}>
        <div className="crs-av" aria-hidden="true"><PersonIcon size={20} /></div>
        <div className="crs-l1">
          <h1>{r.name || fmtPhone(r.phone)}</h1>
          {r.name && <><span className="crs-d">·</span><span className="crs-pn">{fmtPhone(r.phone)}</span></>}
          <span className="crs-d">·</span><span className="crs-lg">{langCode(r.language)}</span>
          {r.referenceNo && <><span className="crs-d">·</span><span className="crs-ref">{formatRef(r.referenceNo)}</span></>}
        </div>
        <div className="crs-l2">
          {r.channel === 'voicemail' ? 'Voicemail' : r.channel === 'missed' ? 'Missed call' : 'Call'} · {callWhen(r.startedAt)}{dur(r.durationSeconds) ? ` · ${dur(r.durationSeconds)}` : ''}
          {` · ${ctName}`}{line ? ` · ${line}` : ''}
        </div>
        <div className="crs-l3">
          {catName && <span className="crs-cat" style={catColor ? { background: catColor } : undefined}>{catName}</span>}
          {has('labels') && (r.labels ?? []).map((l) => (
            <span key={l} className="crs-lab"><i style={{ background: props.labelColors?.[l] ?? '#94a3b8' }} />{l}</span>
          ))}
        </div>
        <div className="crs-rc">
          {has('status') && <div><span className="crs-cap">Status</span><span className={`crs-ch ${isDone(r) ? 'done' : 'st'}`}>{isDone(r) ? 'Completed' : 'Pending'}</span>{isUrgent(r) && <span className="crs-ch urg">Urgent</span>}</div>}
          {has('reminder') && fu && <div><span className="crs-ch rem">{fu}</span></div>}
          {has('status') && <div><span className="crs-cap">Assigned to</span><span className="crs-ch asg">{r.assignedTo || 'Unassigned'}</span></div>}
        </div>
      </div>

      {has('summary') && (rows || paragraph) && (
        <div className="crs-sec">
          <div className="crs-sh">What the caller needs</div>
          {rows ? (
            <dl className="crs-kv">
              <dt>Caller</dt><dd>{rows.caller}</dd>
              <dt>Wants</dt><dd className="big">{rows.wants}</dd>
              {rows.outcome && <><dt>Outcome</dt><dd>{rows.outcome}</dd></>}
              <dt>Next step</dt><dd><span className="crs-next">{rows.next}</span></dd>
              {rows.flags && <><dt>Flags</dt><dd className="crs-flags">{rows.flags}</dd></>}
            </dl>
          ) : <p className="crs-par">{paragraph}</p>}
        </div>
      )}

      {has('caller') && fields.length > 0 && (
        <div className="crs-sec">
          <div className="crs-sh">Call data <span>{fields.length - missing} of {fields.length} captured{corrected > 0 ? ` · ${corrected} corrected by the caller` : ''}</span></div>
          <div className="crs-cd">
            {[fields.slice(0, half), fields.slice(half)].map((col, i) => (
              <div key={i}>{col.map((f) => (
                <div key={f.token} className={`crs-row${f.notCaptured ? ' nc' : ''}`}>
                  <span>{f.label}</span>
                  {f.notCaptured ? <span>not captured</span> : (
                    <span>{f.value}{f.corrected && f.previous && <s className="crs-was">{f.previous}</s>}{f.corrected && <b className="crs-fix">CORRECTED</b>}{f.flag === 'flagged' && <b className="crs-flag">FLAGGED</b>}{f.source === 'voicemail' && <i className="crs-vm"> (from voicemail)</i>}</span>
                  )}
                </div>
              ))}</div>
            ))}
          </div>
        </div>
      )}

      {has('notes') && notes.length > 0 && (
        <div className="crs-sec">
          <div className="crs-sh">What the practice did <span>notes and texts, newest first</span></div>
          <ul className="crs-notes">
            {notes.map((n) => (
              <li key={n.id}>
                <span>{n.at ? shortWhen(n.at) : ''} · {n.author}{n.kind === 'text-sent' ? ' · Text sent' : n.kind === 'text-failed' ? ' · Text not sent' : ''}</span>
                {n.kind === 'text-sent' || n.kind === 'text-failed' ? <div className="crs-txt">{n.text}</div> : <span>{n.text}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {has('transcript') && spoken && (
        <>
          <div className="crs-more">Transcript on page 2.</div>
          <div className="crs-sec crs-trsec">
            <div className="crs-sh">Transcript <span>as spoken</span></div>
            <div className="crs-tr">
              {lines.map((l, i) => isPath(l) ? (
                <div key={i} className="crs-l crs-path"><span className="crs-t">{clock(l.ts)}</span><div className="crs-s">Call path</div><div className="crs-p">{(l.text ?? '').replace(/^\s*\[path\]\s*/, '')}</div></div>
              ) : (
                <div key={i} className={`crs-l${l.role === 'agent' ? ' ag' : ''}`}>
                  <span className="crs-t">{clock(l.ts)}</span>
                  <div className={`crs-s ${l.role === 'agent' ? 'a2' : 'c2'}`}>
                    {l.role === 'agent' ? <SparkIcon /> : <PersonIcon size={13} />}
                    {l.role === 'agent' ? `${(l.speaker || 'Agent').toUpperCase()} · AI` : callerFirst}
                    {l.lang && <span className="crs-lg2">{l.lang.toUpperCase()}</span>}
                  </div>
                  <div>{l.text}</div>
                  {props.english?.[(r.transcript ?? []).indexOf(l)] && <div className="crs-es">{props.english[(r.transcript ?? []).indexOf(l)]}</div>}
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/** The practice name for the print header (the same query as Reports).
 *  v12.10.15 (#1627): the name Messages already keeps in the session (vs.tenantDisplayName, written when the org loads) is used
 *  straight away, so a print fired before the query answers never says "Your practice"; the query is only the fallback / refresh. */
export function sessionPracticeName(): string {
  try { return sessionStorage.getItem('vs.tenantDisplayName')?.trim() || ''; } catch { return ''; }
}
export function usePracticeName(): string {
  const org = useQuery<{ identity?: { displayName?: string; legalName?: string } }>({ queryKey: ['t2-org-name'], queryFn: () => api.get('/t2/settings/organization'), staleTime: 300_000 });
  const n = (org.data?.identity?.displayName || org.data?.identity?.legalName || '').trim();
  if (n) { try { sessionStorage.setItem('vs.tenantDisplayName', n); } catch { /* blocked */ } }
  return n || sessionPracticeName() || 'Your practice';
}

export type CrsAction = 'print' | 'summary' | 'pdf' | 'html';
/**
 * v12.10.14 (#1619): print one call RIGHT HERE - no new tab, the screen stays. Reads the full record, draws the sheet off screen,
 * then prints (Print · with transcript / Print · summary only / Download PDF = the print window's Save as PDF) or saves the HTML.
 * The same audit entry as Reports › Call record. `onDone` runs once the job is handed to the browser (or failed).
 */
export function CallRecordPrint(props: { id: string; action: CrsAction; withPath?: boolean; english?: (string | null)[]; labelColors?: Record<string, string>;
  categoryColor?: string; categoryName?: string; onDone: () => void }) {
  const practice = usePracticeName();
  const rec = useQuery<CrsRecord>({ queryKey: ['t2-callrecord-one', props.id], queryFn: () => api.get(`/t2/surface/messages/${encodeURIComponent(props.id)}`), staleTime: 0 });
  const ref = useRef<HTMLDivElement>(null);
  const fired = useRef(false);
  const run = useRef(etTime());
  useEffect(() => {
    if (rec.isError && !fired.current) { fired.current = true; toast('error', 'Could not print', 'The call record could not be read - try again.'); props.onDone(); return; }
    if (!rec.data || !ref.current || fired.current) return;
    fired.current = true;
    const r = rec.data;
    const withTr = props.action !== 'summary';
    void api.post('/t2/reports/call-record/audit', { action: props.action === 'html' ? 'html' : props.action === 'pdf' ? 'pdf' : 'print', calls: 1, refs: [formatRef(r.referenceNo)],
      parts: withTr ? CRS_ALL : CRS_ALL.filter((p) => p !== 'transcript'), from: 'drawer' }).catch(() => { /* the print still happens */ });
    const node = ref.current;
    const t = window.setTimeout(() => {
      if (props.action === 'html') {
        exportHtml({ node, wrapClass: 'vsr-root cr-root', title: `Call record - ${practice} - ${formatRef(r.referenceNo)}`,
          fileName: `Call-record_${fileSafe(practice)}_${(r.referenceNo ?? r.startedAt.slice(0, 10)).replace(/\D/g, '')}.html`, prefixes: ['.vsr-', '.cr-', '.crs'] });
      } else {
        if (props.action === 'pdf') toast('info', 'Save as PDF', 'Choose "Save as PDF" in the print window.');
        printReport({ node, wrapClass: 'vsr-root cr-root', running: `${practice} · Call record · ${formatRef(r.referenceNo)} · ${r.name || fmtPhone(r.phone)}`, phi: true });
      }
      props.onDone();
    }, 60);
    return () => window.clearTimeout(t);
  }, [rec.data, rec.isError]);   // eslint-disable-line react-hooks/exhaustive-deps
  if (!rec.data) return null;
  const r = rec.data;
  return createPortal(
    <div className="crs-offscreen vsr-root cr-root" aria-hidden="true">
      <div className="vsr-card" ref={ref}>
        <PrintHeader practice={practice} report="Call record" period={new Date(r.startedAt).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })} run={run.current} />
        <CallRecordSheet r={r} parts={props.action === 'summary' ? CRS_ALL.filter((p) => p !== 'transcript') : CRS_ALL} withPath={props.withPath}
          english={props.english} labelColors={props.labelColors} categoryColor={props.categoryColor} categoryName={props.categoryName} />
        <PrintFooter printed={run.current} phi />
      </div>
    </div>,
    document.body,
  );
}
